import { FC, forwardRef, type ReactNode, useState } from "react";

import { noop } from "lodash";
import {
  Box,
  Popover,
  PopoverTrigger,
  Column,
  Portal,
  Row,
  CheckIcon,
} from "@hightouch/htui";

import { RouterLink } from "src/router";
import { withPermission } from "src/components/permission";
import { useNavWidth } from "src/contexts/nav-width-context";
import { useSpaceContext } from "src/components/spaces";

import { NavItem, PermissionedNavItem } from "./nav-item";
import {
  NAV_POPOVER_MENU_ITEM_STYLES,
  NavPopoverContentAndBody,
  PopoverHeader,
} from "./nav-popovers";
import { ActiveSectionProps, useActiveLink } from "./utils";

export type NavSectionLink = {
  isSub?: boolean;
  href: string;
  label: string;
  rightElement?: ReactNode;
  permission?: any;
  icon?: React.ElementType;
  /**
   * Additional pathnames (same `/:section/:page` shape as `href`) that should
   * also mark this link as active. Useful when a single nav entry represents
   * multiple peer routes.
   */
  activePaths?: string[];
};

type BaseNavSectionProps = {
  title: string;
  validLinks: NavSectionLink[];
  rightElement?: ReactNode;
  icon: React.ElementType;
  onChange?: (name: string | null) => void;
};

type NavSectionStateProps = {
  isOpen: boolean;
  isActiveSection: boolean;
};

export type NavSectionProps = ActiveSectionProps & {
  isEnabled?: boolean;
  isSub?: boolean;
  isInFooter?: boolean;
  title: string;
  rightElement?: ReactNode;
  links: Array<NavSectionLink | false>;
  icon: React.ElementType;
  onChange?: (name: string | null) => void;
};

type ExpandedNavSectionProps = BaseNavSectionProps &
  NavSectionStateProps & {
    isSub?: boolean;
    defaultLink?: NavSectionLink;
  };

type CollapsedNavSectionProps = BaseNavSectionProps &
  NavSectionStateProps & {
    isOpen?: boolean;
  };

type MenuLinkProps = NavSectionLink & {
  isDisabled?: boolean;
  onClick: () => void;
};

export const NavSection: FC<Readonly<NavSectionProps>> = ({
  isEnabled,
  isSub,
  isInFooter,
  activeSection,
  openSection,
  icon,
  title,
  rightElement,
  links,
  onChange,
}) => {
  const { isNavOpen } = useNavWidth();
  const { isInSpace, isPathOrChildrenAccessibleInSpace } = useSpaceContext();

  // Filter out falsy links, then filter by space access if in a space
  const validLinks = links
    .filter((x): x is NavSectionLink => Boolean(x))
    .filter(
      (link) => !isInSpace || isPathOrChildrenAccessibleInSpace(link.href),
    );
  const defaultLink = validLinks[0];

  // Footer sections still toggle open / closed, even if "all" are open in the primary nav.
  const isOpen = isInFooter
    ? activeSection === title
    : openSection === "all" || openSection === title;
  const isActiveSection = activeSection === title;

  // Hide section if disabled or if no links remain after space filtering
  if (!isEnabled || validLinks.length === 0) {
    return null;
  }

  const sharedProps = {
    isOpen,
    isActiveSection,
    validLinks,
    title,
    rightElement,
    icon,
    onChange,
  };

  return isNavOpen ? (
    <ExpandedNavSection
      isSub={isSub}
      defaultLink={defaultLink}
      {...sharedProps}
    />
  ) : (
    <CollapsedNavSection {...sharedProps} />
  );
};

const indicatorLeft = "17px"; // var(--chakra-space-4) + 1px offset

const ExpandedNavSection: FC<ExpandedNavSectionProps> = ({
  isOpen,
  isActiveSection,
  isSub,
  validLinks,
  defaultLink,
  title,
  rightElement,
  icon,
  onChange,
}) => {
  // Hover item is the href of the nested nav item
  const [hoverItem, setHoverItem] = useState<string | null>(null);

  const handleSectionClick = () => {
    if (!isOpen) {
      onChange?.(title);
    }
  };

  return (
    <Column gap={isOpen ? 2 : 0}>
      <NavItem
        href={defaultLink?.href}
        isActiveSection={isActiveSection}
        icon={icon}
        label={title}
        rightElement={rightElement}
        onClick={handleSectionClick}
      />
      {validLinks.length > 0 && (
        <Column
          display="grid"
          gridTemplateRows={isOpen ? "1fr" : "0fr"}
          overflowY="hidden"
          willChange={isOpen ? "grid-template-rows" : "none"}
          transitionProperty="grid-template-rows"
          transitionDuration="slow"
          transitionTimingFunction="easeInOutQuad"
          visibility={isOpen ? "visible" : "hidden"}
          _focusWithin={{
            overflowY: "visible !important",
          }}
        >
          <Column pos="relative" minHeight={0}>
            {/* Indicator rail */}
            <Box
              aria-hidden="true"
              bg="gray.300"
              borderRadius="full"
              pos="absolute"
              left={indicatorLeft}
              top={2}
              bottom={2}
              width="2px"
            />
            {validLinks.map((link) => (
              <PermissionedNavItem
                key={link.href}
                isNested
                isSub={isSub}
                isHovered={hoverItem === link.href}
                onMouseOver={() => setHoverItem(link.href)}
                onMouseLeave={() => setHoverItem(null)}
                onFocus={() => setHoverItem(link.href)}
                {...link}
              />
            ))}
          </Column>
        </Column>
      )}
    </Column>
  );
};

const CollapsedNavSection: FC<CollapsedNavSectionProps> = ({
  isOpen,
  isActiveSection,
  validLinks,
  title,
  icon,
  onChange,
}) => {
  const handleMenuClick = () => {
    onChange?.(title);
  };

  return (
    // Box wrapper required so nav gap does not apply to the popover
    <Box>
      <Popover trigger="hover" placement="right-start" offset={[0, 16]}>
        {({ isOpen: isPopoverOpen }) => (
          <>
            <PopoverTrigger>
              <NavItem
                isActive={isOpen && isActiveSection}
                isHovered={isPopoverOpen}
                hasCollapsedTooltip={false} // Disable tooltip since it has a popover
                icon={icon}
                label={title}
                onClick={noop} // Required for nav item - would be nice to rework this
              />
            </PopoverTrigger>

            <Portal>
              <NavPopoverContentAndBody>
                <PopoverHeader title={title} />
                {validLinks.map((link) => (
                  <PermissionedMenuLink
                    key={link.href}
                    {...link}
                    onClick={handleMenuClick}
                  />
                ))}
              </NavPopoverContentAndBody>
            </Portal>
          </>
        )}
      </Popover>
    </Box>
  );
};

const MenuLink = forwardRef<HTMLDivElement, MenuLinkProps>((props, ref) => {
  const isActive = useActiveLink(props);

  return (
    <Box
      ref={ref}
      as={props.isDisabled ? "button" : RouterLink}
      // @ts-expect-error - `as` prop
      to={props.href}
      preload="intent"
      display="flex"
      alignItems="center"
      bg={isActive ? "primary.background" : "white"}
      color={isActive ? "primary.base" : "text.primary"}
      h={10}
      justifyContent="space-between"
      opacity={props.isDisabled ? 0.5 : 1}
      pointerEvents={props.isDisabled ? "none" : "auto"}
      _hover={isActive ? undefined : { bg: "base.background" }}
      onClick={props.onClick}
      {...NAV_POPOVER_MENU_ITEM_STYLES}
    >
      {props.label}
      {(props.rightElement || isActive) && (
        <Row align="center" gap={2}>
          {props.rightElement}
          {isActive && (
            <Box as={CheckIcon} color="primary.base" fontSize="lg" />
          )}
        </Row>
      )}
    </Box>
  );
});

MenuLink.displayName = "MenuLink";

const PermissionedMenuLink = withPermission(MenuLink);
